// src/pages/public/CartConfirmation.jsx
import React, { useEffect, useState } from 'react'
import { Link, useParams, useLocation } from 'react-router-dom'
import { CheckCircle2, Package, ArrowRight, Loader2, Mail, Truck } from 'lucide-react'
import api from '../../lib/axios'

const fmt = (n, currency = 'MAD') => {
  const symbol = currency === 'MAD' ? 'DH' : currency === 'EUR' ? '€' : currency
  return Number(n).toFixed(0) + ' ' + symbol
}

export default function CartConfirmation() {
  const { groupRef } = useParams()
  const location = useLocation()
  const state = location.state || {}

  const [orders, setOrders]         = useState(state.orders ?? [])
  const [grandTotal, setGrandTotal] = useState(state.grandTotal ?? 0)
  const [loading, setLoading]       = useState(!state.orders)

  useEffect(() => {
    if (state.orders?.length) return
    let cancelled = false
    api.get(`/public/cart/${groupRef}`)
      .then(({ data }) => {
        if (cancelled) return
        setOrders(data?.orders ?? [])
        setGrandTotal(data?.grand_total ?? 0)
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false))
    return () => { cancelled = true }
  }, [groupRef, state.orders])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f9f9ff]">
        <Loader2 className="w-8 h-8 text-[#006b5f] animate-spin" />
      </div>
    )
  }

  const currency = orders[0]?.currency || 'MAD'

  return (
    <div className="min-h-screen bg-[#f9f9ff] py-16">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@500;600;700;800&display=swap');
        .font-display{font-family:'Plus Jakarta Sans',system-ui,sans-serif}
        .font-body{font-family:'Inter',system-ui,sans-serif}
        @keyframes pulse{0%,100%{box-shadow:0 0 0 0 rgba(0,107,95,.35)}50%{box-shadow:0 0 0 18px rgba(0,107,95,0)}}
      `}</style>

      <div className="max-w-3xl mx-auto px-6 font-body">
        <div className="bg-white rounded-3xl shadow-xl p-8 sm:p-10 text-center">
          <div className="mx-auto w-20 h-20 rounded-full bg-[#dcf5ea] flex items-center justify-center mb-6"
               style={{ animation: 'pulse 2s ease-in-out infinite' }}>
            <CheckCircle2 className="w-10 h-10 text-[#006b5f]" />
          </div>
          <h1 className="font-display font-bold text-3xl text-[#000a1e] mb-3">Merci pour votre commande !</h1>
          <p className="text-sm text-slate-500 mb-8">
            Nous avons bien reçu votre panier. Vous recevrez une confirmation par e-mail.
          </p>

          <div className="inline-flex items-center gap-2 bg-[#eff2ff] px-4 py-2 rounded-full mb-8">
            <span className="text-xs font-semibold text-[#006b5f]">Référence panier :</span>
            <span className="font-mono font-bold text-sm text-[#000a1e]">{groupRef}</span>
          </div>

          <div className="text-left space-y-2 mb-8">
            {orders.map((o) => (
              <div key={o.id} className="flex items-center gap-3 p-3 rounded-2xl border border-slate-100">
                {o.product?.image ? (
                  <img src={o.product.image} alt="" className="w-12 h-12 rounded-xl object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-xl bg-[#eff2ff] flex items-center justify-center">
                    <Package className="w-5 h-5 text-[#006b5f]/40" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#000a1e] truncate">{o.product?.name}</p>
                  <p className="text-xs text-slate-500">× {o.quantity} — Réf. {o.reference}</p>
                </div>
                <p className="font-display font-bold text-sm text-[#000a1e]">{fmt(o.total, o.currency)}</p>
              </div>
            ))}
          </div>

          <div className="flex justify-between items-baseline py-5 border-t border-b border-slate-100 mb-8">
            <span className="text-sm font-semibold text-[#000a1e]">Total à la livraison</span>
            <span className="font-display font-extrabold text-2xl text-[#000a1e]">
              {fmt(grandTotal, currency)}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8 text-left">
            <div className="flex items-start gap-3 p-4 bg-[#f9f9ff] rounded-2xl">
              <Mail className="w-5 h-5 text-[#006b5f] shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-[#000a1e]">E-mail de confirmation</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {state.customerEmail || 'Nous vous envoyons le récapitulatif.'}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-[#f9f9ff] rounded-2xl">
              <Truck className="w-5 h-5 text-[#006b5f] shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-[#000a1e]">Livraison</p>
                <p className="text-xs text-slate-500 mt-0.5">48h ouvrées — offerte</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/produits"
              className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl border border-slate-200 font-semibold text-sm text-[#000a1e] hover:bg-slate-50 transition">
              Continuer mes achats
            </Link>
            <Link to="/"
              className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl bg-[#006b5f] hover:bg-[#005a50] text-white font-semibold text-sm shadow-lg shadow-[#006b5f]/20 transition-all hover:scale-[1.02] active:scale-95">
              Retour à l'accueil <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          Conservez cette référence pour suivre votre commande : <span className="font-mono">{groupRef}</span>
        </p>
      </div>
    </div>
  )
}